import { Inject, Injectable } from '@angular/core';
import { EmailBuilderDataService, EmailContentData } from '@galaxy/email-ui/email-builder';
import { TranslateService } from '@ngx-translate/core';
import { CampaignStepType, SenderType, TemplateReferenceInterface } from '@vendasta/campaigns';
import { AppNamespace, OwnerType } from '@vendasta/email-builder';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ObservableWorkState, WorkStates } from '@vendasta/rx-utils/work-state';
import { catchError, Observable, of, throwError } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { Context } from './base-template-editing/context';
import { TemplateExporter } from './base-template-editing/dependencies';
import { CampaignsService } from './marketing-automation-page/campaigns.service';

export interface SaveResult {
  templateId: string;
  campaignStepId: string;
}

export interface TemplateService {
  createNewTemplate$(
    ownerType: OwnerType,
    partnerId: string,
    namespace: AppNamespace,
    templateId: string,
    data: EmailContentData,
    html: string,
  ): Observable<boolean>;

  updateExistingTemplate$(
    ownerType: OwnerType,
    partnerId: string,
    namespace: AppNamespace,
    templateId: string,
    data: EmailContentData,
    html: string,
  ): Observable<boolean>;

  validateTemplate(data: EmailContentData): boolean;
}

export interface TemplateIDGenerator {
  generate(): string;
}

@Injectable({ providedIn: 'root' })
class UUIDTemplateIDGenerator implements TemplateIDGenerator {
  generate(): string {
    return `EMAIL-BUILDER-${uuidv4()}`;
  }
}

@Injectable()
export class EmailTemplateSaver implements TemplateExporter {
  private latestData?: EmailContentData;
  private latestHTML?: string;

  private workState$ = new ObservableWorkState<SaveResult>();
  public readonly workState: WorkStates<SaveResult> = this.workState$;

  constructor(
    @Inject(EmailBuilderDataService) private readonly emailBuilderDataService: TemplateService,
    private readonly campaignsService: CampaignsService,
    @Inject(UUIDTemplateIDGenerator) private readonly idGenerator: TemplateIDGenerator = new UUIDTemplateIDGenerator(),
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
  ) {
    this.initialize();
  }

  initialize(): void {
    this.latestData = {} as EmailContentData;
    this.latestHTML = '';
  }

  saveWithWorkState$(
    partnerId: string,
    marketId: string,
    emailTemplateContext: Context,
    campaignId: string,
    campaignStepId?: string,
    parent?: TemplateReferenceInterface,
  ): Observable<SaveResult> {
    const o$ = this.doSave(partnerId, marketId, emailTemplateContext, campaignId, campaignStepId, parent).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );
    this.workState$.startWork(o$);
    return o$;
  }

  save$(
    partnerId: string,
    marketId: string,
    emailTemplateContext: Context,
    campaignId: string,
    campaignStepId?: string,
    parent?: TemplateReferenceInterface,
  ): Observable<SaveResult> {
    return this.doSave(partnerId, marketId, emailTemplateContext, campaignId, campaignStepId, parent);
  }

  private doSave(
    partnerId: string,
    marketId: string,
    emailTemplateContext: Context,
    campaignId: string,
    campaignStepId?: string,
    _parent?: TemplateReferenceInterface,
  ): Observable<SaveResult> {
    if (!this.latestData) {
      throwError(() => new Error('no email content data'));
    }
    if (!this.latestHTML) {
      throwError(() => new Error('no email HTML'));
    }

    const createOrUpdateArgs = {
      partnerId: partnerId,
      data: this.latestData,
      html: this.latestHTML,
    };
    if (emailTemplateContext.isNew()) {
      return this.createNew(campaignId, createOrUpdateArgs);
    } else if (emailTemplateContext.isReplacingLegacy()) {
      return this.replaceLegacy(campaignId, campaignStepId, createOrUpdateArgs, emailTemplateContext);
    } else {
      return this.updateExisting(campaignId, campaignStepId, createOrUpdateArgs, emailTemplateContext);
    }
  }

  private updateExisting(
    campaignID: string,
    campaignStepID: string,
    updateArgs: {
      partnerId: string;
      data: EmailContentData;
      html: string;
    },
    ctx: Context,
  ): Observable<SaveResult> {
    return this.emailBuilderDataService
      .updateExistingTemplate$(
        OwnerType.OWNER_TYPE_PARTNER,
        updateArgs.partnerId,
        AppNamespace.CAMPAIGNS,
        ctx.getExistingTemplateId(),
        updateArgs.data,
        updateArgs.html,
      )
      .pipe(
        catchError((err) => {
          this.snackbarService.openErrorSnack(this.translate.instant('EMAIL_BUILDER.SAVING.GENERIC_ERROR'));
          throw new Error(err);
        }),
        switchMap((ebUpdateSucceeded) => {
          if (!ebUpdateSucceeded) {
            return;
          }
          const templateID = ctx.getExistingTemplateId();
          if (campaignStepID) {
            return this.campaignsService.updateStep(
              { type: SenderType.SENDER_TYPE_PARTNER, id: updateArgs.partnerId },
              campaignID,
              campaignStepID,
              templateID || this.latestData.id,
              this.latestData.name,
              0,
              { paths: ['name', 'templateId'] },
            );
          }
          console.error('No campaign step ID provided');
        }),
      );
  }

  private replaceLegacy(
    campaignID: string,
    campaignStepID: string,
    replaceArgs: {
      partnerId: string;
      data: EmailContentData;
      html: string;
    },
    ctx: Context,
  ): Observable<SaveResult> {
    const isValid = this.emailBuilderDataService.validateTemplate(replaceArgs.data);
    if (!isValid) {
      return;
    }

    const templateID$ = this.campaignsService
      .updateStep(
        { type: SenderType.SENDER_TYPE_PARTNER, id: replaceArgs.partnerId },
        campaignID,
        campaignStepID,
        ctx.getExistingTemplateId() || this.latestData.id,
        this.latestData.name,
        0,
        { paths: ['name', 'templateId'] },
      )
      .pipe(map((resp) => resp.templateId));
    return templateID$.pipe(
      switchMap((templateID) => {
        return this.emailBuilderDataService
          .updateExistingTemplate$(
            OwnerType.OWNER_TYPE_PARTNER,
            replaceArgs.partnerId,
            AppNamespace.CAMPAIGNS,
            templateID,
            replaceArgs.data,
            replaceArgs.html,
          )
          .pipe(
            map(() => ({
              templateId: templateID,
              campaignStepId: campaignStepID,
            })),
          );
      }),
    );
  }

  private createNew(
    campaignID: string,
    createArgs: {
      partnerId: string;
      data: EmailContentData;
      html: string;
    },
  ): Observable<SaveResult> {
    const templateId = this.idGenerator.generate();
    return this.emailBuilderDataService
      .createNewTemplate$(
        OwnerType.OWNER_TYPE_PARTNER,
        createArgs.partnerId,
        AppNamespace.CAMPAIGNS,
        templateId,
        createArgs.data,
        createArgs.html,
      )
      .pipe(
        catchError((err) => {
          this.snackbarService.openErrorSnack(this.translate.instant('EMAIL_BUILDER.SAVING.GENERIC_ERROR'));
          throw new Error(err);
        }),
        switchMap((ebTemplateSucceeded) => {
          if (!ebTemplateSucceeded) {
            return of(null);
          }
          return this.campaignsService.addStep(
            { type: SenderType.SENDER_TYPE_PARTNER, id: createArgs.partnerId },
            campaignID,
            CampaignStepType.CAMPAIGN_STEP_TYPE_EMAIL,
            templateId || this.latestData.id,
            this.latestData.name,
          );
        }),
      );
  }

  updateContentData(data: EmailContentData): void {
    this.latestData = data;
  }

  updateHTML(html: string): void {
    this.latestHTML = html;
  }

  getContentData(): EmailContentData {
    return this.latestData;
  }

  getHTML(): string {
    return this.latestHTML;
  }
}

export function getStepIdFromCreateResponse(templateId: string, response: string): string {
  const parsed = JSON.parse(response);
  const steps: Array<any> = parsed['campaign_schedule'];
  const lastSteps = steps.reverse();
  for (let i = 0; i < lastSteps.length; i++) {
    if (lastSteps[i]?.template_id === templateId) {
      return lastSteps[i].campaign_step_id;
    }
  }
  throw new Error('Could not extract campaign step ID from response');
}
